import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { qs } from './api.ts';
import { shiftDate, todayMarket } from './format.ts';

/**
 * `preset` is a named range ("month", "ytd", "all"…) re-resolved on every load; '' means custom dates.
 * `needs` ('1' when on) is "Needs notes": only the trades still to journal. Only the pages that offer it send it
 * (Journal, Trades), so it stays out of `query` and `isDefault`.
 */
export type Filters = {
  preset: string; from: string; to: string;
  symbol: string; tag: string; side: string; result: string; setup: string; mistake: string; account: string; needs: string;
};
const DEFAULT_PRESET = 'month';
// v2: v1 saved "All" for everyone; the default is now this month.
const KEY = 'tj_filters_v2';

const blank = () => ({ symbol: '', tag: '', side: '', result: '', setup: '', mistake: '', account: '', needs: '' });
const defaults = (): Filters => ({ ...blank(), preset: DEFAULT_PRESET, ...presetRange(DEFAULT_PRESET) });

const Ctx = createContext<{ filters: Filters; setFilters: (f: Partial<Filters>) => void; reset: () => void; query: string; isDefault: boolean }>(null!);
export const useFilters = () => useContext(Ctx);

export function FiltersProvider({ children }: { children: ReactNode }) {
  const [filters, set] = useState<Filters>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<Filters> | null;
      if (!saved) return defaults();
      // A saved preset like "This month" means this month now, not the month it was saved in.
      const f = { ...defaults(), ...saved };
      return f.preset ? { ...f, ...presetRange(f.preset) } : f;
    } catch { return defaults(); }
  });
  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(filters)); } catch { /* ignore */ }
  }, [filters]);
  const value = useMemo(() => {
    const { preset: _p, needs: _n, ...params } = filters;
    const d = defaults();
    return {
      filters,
      // Picking dates by hand turns the preset off; picking a preset fills in its dates.
      setFilters: (f: Partial<Filters>) => set((prev) => ({ ...prev, ...(('from' in f || 'to' in f) && !('preset' in f) ? { preset: '' } : {}), ...f })),
      reset: () => set(defaults()),
      query: qs(params),
      isDefault: (Object.keys(d) as (keyof Filters)[]).every((k) => k === 'needs' || filters[k] === d[k]),
    };
  }, [filters]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function presetRange(preset: string): { from: string; to: string } {
  const today = todayMarket();
  const d = new Date(today + 'T12:00:00Z');
  switch (preset) {
    case 'today': return { from: today, to: today };
    case 'week': {
      const dow = (d.getUTCDay() + 6) % 7; // Monday start
      return { from: shiftDate(today, -dow), to: today };
    }
    case '30d': return { from: shiftDate(today, -29), to: today };
    case 'month': return { from: today.slice(0, 8) + '01', to: today };
    case 'ytd': return { from: today.slice(0, 5) + '01-01', to: today };
    default: return { from: '', to: '' };
  }
}
